import { AccountInfo } from '@azure/msal-browser';
import {
    getSessionToken as getDescopeToken,
    useDescope,
    useSession,
    useUser
} from '@descope/vue-sdk';
import * as Sentry from '@sentry/vue';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import apiClient from '@/services/api_client';
import {
    getMicrosoftToken,
    handleMicrosoftConnection,
    logoutMicrosoft
} from '@/services/auth_microsoft';
import { useUserStore } from '@/stores/user';

function getDescopeAuth(isDescopeEnabled: boolean) {
    if (!isDescopeEnabled) {
        return {
            isDescopeAuthenticated: ref(false),
            isDescopeLoading: ref(false),
            descopeUser: ref(null)
        };
    }

    const {
        isAuthenticated: isDescopeAuthenticated,
        isLoading: isDescopeLoading
    } = useSession();
    const { user: descopeUser } = useUser();
    return { isDescopeAuthenticated, isDescopeLoading, descopeUser };
}

export const useAuthStore = defineStore('auth', () => {
    const { logout: descopeLogout } = useDescope();
    const isDescopeEnabled =
        import.meta.env.VITE_DESCOPE_PROJECT_ID !== 'disabled';
    const { isDescopeAuthenticated, isDescopeLoading, descopeUser } =
        getDescopeAuth(isDescopeEnabled);
    const userStore = useUserStore();

    const isAuthenticated = ref<boolean>(false);
    const isLoading = ref<boolean>(true);
    const isInsideTeams = ref(false);
    /**
     * Every delegated scope the tenant has consented to, not only the ones sign-in asked for.
     * The backend wires a tool only when its scope is in here, so an empty list -- before the
     * session's first acquisition -- fails closed.
     */
    const microsoftScopes = ref<string[]>([]);
    const microsoftClientId = ref<string>();
    const microsoftAccount = ref<AccountInfo>();
    /** Which tenant the three above were loaded for, so a workspace switch can tell them from
     *  the deployment-wide account `handleAuth` signs in with when Descope is off. */
    const credentialsTenantId = ref<string>();
    const microsoftRedirectHash = ref<string>('');
    const azureTenantId = ref<string>('');
    const userToken = ref<string | undefined>('');
    const isMicrosoftUserDataLoaded = ref<boolean>(false);
    const authInitialized = ref<boolean>(false);
    const isMicrosoftCredentialsLoaded = ref<boolean>(false);

    const userDataLoadedPromise = new Promise<void>((resolve) => {
        watch(
            [() => descopeUser.value, () => isMicrosoftUserDataLoaded.value],
            ([descopeUserValue, isMicrosoftUserDataLoadedValue]) => {
                if (descopeUserValue || isMicrosoftUserDataLoadedValue)
                    resolve();
            },
            { immediate: true }
        );
    });

    const authReadyPromise = new Promise<void>((resolve) => {
        watch(
            () => isLoading.value,
            (loading) => {
                if (!loading) resolve();
            },
            { immediate: true }
        );
    });

    watch(isDescopeAuthenticated, updateState);
    watch(isDescopeLoading, updateState);

    function updateState() {
        isAuthenticated.value =
            isDescopeAuthenticated.value || isInsideTeams.value;
        isLoading.value = isDescopeLoading.value && !isInsideTeams.value;
    }

    async function handleAuth() {
        if (authInitialized.value) return;

        await checkTeams();
        if (isInsideTeams.value || isDescopeEnabled) {
            authInitialized.value = true;
            return;
        }

        try {
            const credentials = await apiClient.getMsalCredentials();
            const account = await userStore.fetchMicrosoftUser(
                credentials.microsoft_app_id,
                credentials.azure_tenant_id,
                microsoftRedirectHash.value,
                credentials.tenant_name
            );

            if (account) {
                microsoftAccount.value = account;
                microsoftClientId.value = credentials.microsoft_app_id;
                azureTenantId.value = credentials.azure_tenant_id;
                isMicrosoftUserDataLoaded.value = true;
                isAuthenticated.value = true;
            }
        } catch (error) {
            console.error('Error during authentication:', error);
        } finally {
            isLoading.value = false;
            authInitialized.value = true;
        }
    }

    async function checkTeams() {
        try {
            const { app } = await import('@microsoft/teams-js');
            await app.initialize();
            isInsideTeams.value = true;
            await userStore.fetchTeamsUser();
            isMicrosoftUserDataLoaded.value = true;
        } catch (e) {
            isInsideTeams.value = false;
        }
        updateState();
    }

    async function getSessionToken() {
        if (isInsideTeams.value) {
            const { authentication } = await import('@microsoft/teams-js');
            return await authentication.getAuthToken();
        }
        if (!isDescopeEnabled) {
            return await aquireMicrosoftToken();
        }
        return getDescopeToken();
    }

    async function waitForUserData(timeoutMs = 2000) {
        if (descopeUser.value || isMicrosoftUserDataLoaded.value) {
            return true;
        }

        return await Promise.race([
            userDataLoadedPromise.then(() => true),
            new Promise<boolean>((resolve) =>
                setTimeout(() => resolve(false), timeoutMs)
            )
        ]);
    }

    async function waitForAuthInitialized(timeoutMs = 5000) {
        if (authInitialized.value) return true;

        return await Promise.race([
            new Promise<boolean>((resolve) => {
                const stop = watch(
                    () => authInitialized.value,
                    (initialized) => {
                        if (initialized) {
                            stop();
                            resolve(true);
                        }
                    },
                    { immediate: true }
                );
            }),
            new Promise<boolean>((resolve) =>
                setTimeout(() => resolve(false), timeoutMs)
            )
        ]);
    }

    async function waitForAuthReady(timeoutMs = 3000) {
        if (!isLoading.value) {
            return true;
        }

        return await Promise.race([
            new Promise<boolean>((resolve) => {
                const stop = watch(
                    () => isLoading.value,
                    (loading) => {
                        if (!loading) {
                            stop();
                            resolve(true);
                        }
                    },
                    { immediate: true }
                );
            }),
            new Promise<boolean>((resolve) =>
                setTimeout(() => resolve(false), timeoutMs)
            )
        ]);
    }

    async function updateCredentials(tenantId: string) {
        if (isInsideTeams.value || !tenantId) return;

        try {
            // Another workspace runs on another app registration: kept, the client id below
            // would read as this tenant being configured, and consent would go to the previous
            // tenant's app. Nothing is dropped on a first load or a reload of the same tenant.
            if (
                credentialsTenantId.value &&
                credentialsTenantId.value !== tenantId
            ) {
                microsoftAccount.value = undefined;
                microsoftClientId.value = undefined;
                azureTenantId.value = '';
                microsoftScopes.value = [];
                userToken.value = undefined;
            }
            credentialsTenantId.value = tenantId;
            isMicrosoftCredentialsLoaded.value = false;
            const credentials =
                await apiClient.getSharePointCredentials(tenantId);

            if (!credentials || Object.keys(credentials).length === 0) return;

            const account = await handleMicrosoftConnection(
                credentials.microsoft_app_id,
                credentials.azure_tenant_id,
                descopeUser.value?.email || '',
                microsoftRedirectHash.value
            );

            if (account) {
                microsoftAccount.value = account;
                microsoftClientId.value = credentials.microsoft_app_id;
                azureTenantId.value = credentials.azure_tenant_id;
            }
        } catch (error) {
            console.error('Error updating credentials:', error);
        } finally {
            isMicrosoftCredentialsLoaded.value = true;
        }
    }

    /**
     * Re-read the token past the MSAL cache, for the return from a consent screen: the grant
     * changed a second ago and the cached token predates it.
     *
     * Returns whether it read anything, so the caller does not take "no account yet" for
     * "read, and nothing changed".
     */
    async function refreshMicrosoftScopes(): Promise<boolean> {
        if (
            !microsoftAccount.value ||
            !microsoftClientId.value ||
            !azureTenantId.value
        ) {
            return false;
        }

        try {
            const token = await getMicrosoftToken(
                microsoftAccount.value,
                microsoftClientId.value,
                azureTenantId.value,
                true
            );
            // `getMicrosoftToken` yields nothing when it swallows a non-interaction error, and
            // when it has navigated away to ask for one. Writing empty scopes there would read
            // as "the grant is gone" and let the caller announce a consent it never confirmed.
            if (!token) return false;

            userToken.value = token.accessToken;
            microsoftScopes.value = token.scopes;
            return true;
        } catch (error) {
            console.error('Could not refresh the Microsoft scopes:', error);
            return false;
        }
    }

    async function aquireMicrosoftToken(): Promise<string | undefined> {
        if (isInsideTeams.value) {
            console.log('Retrieving token directly from teams');
            userToken.value = await getSessionToken();
            return userToken.value;
        }
        if (
            microsoftAccount.value &&
            microsoftClientId.value &&
            azureTenantId.value
        ) {
            try {
                const token = await getMicrosoftToken(
                    microsoftAccount.value,
                    microsoftClientId.value,
                    azureTenantId.value
                );
                userToken.value = token?.accessToken;
                microsoftScopes.value = token?.scopes ?? [];
            } catch (error) {
                console.error('Error acquiring token:', error);
            }
        }
        return userToken.value;
    }

    async function handleLogout(): Promise<string | undefined> {
        Sentry.setUser(null);

        if (isInsideTeams.value) return '/';

        if (!isDescopeEnabled) {
            if (microsoftClientId.value && azureTenantId.value) {
                await logoutMicrosoft(
                    microsoftClientId.value,
                    azureTenantId.value
                );
            }
        } else {
            await descopeLogout();
            return '/login';
        }
    }

    const issuer = computed(() => {
        if (isInsideTeams.value) return 'teams';
        if (isDescopeEnabled) return 'descope';
        return 'microsoft';
    });

    return {
        isAuthenticated,
        isMicrosoftCredentialsLoaded,
        isLoading,
        handleAuth,
        isInsideTeams,
        isDescopeEnabled,
        getSessionToken,
        updateCredentials,
        aquireMicrosoftToken,
        userDataLoadedPromise,
        waitForUserData,
        waitForAuthReady,
        waitForAuthInitialized,
        authReadyPromise,
        microsoftRedirectHash,
        handleLogout,
        issuer,
        // Read by the admin consent screen, which builds its own Microsoft URL.
        microsoftClientId,
        azureTenantId,
        microsoftScopes,
        refreshMicrosoftScopes
    };
});
